import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Plus, Pencil, Trash2, Users, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";

const colorOptions = [
  { value: "#002FA7", label: "Blue" },
  { value: "#00C781", label: "Green" },
  { value: "#FFCC00", label: "Yellow" },
  { value: "#FF3B30", label: "Red" },
  { value: "#8B5CF6", label: "Purple" },
  { value: "#F97316", label: "Orange" },
  { value: "#52525B", label: "Gray" },
  { value: "#06B6D4", label: "Cyan" },
];

const Teams = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#002FA7"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, empRes] = await Promise.all([
        axios.get(`${API}/teams`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true })
      ]);
      setTeams(teamsRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTeam) {
        await axios.put(`${API}/teams/${editingTeam.team_id}`, formData, { withCredentials: true });
        toast.success("Team updated");
      } else {
        await axios.post(`${API}/teams`, formData, { withCredentials: true });
        toast.success("Team created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save team");
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      description: team.description || "",
      color: team.color || "#002FA7"
    });
    setDialogOpen(true);
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm("Delete this team? Members will be unassigned.")) return;
    try {
      await axios.delete(`${API}/teams/${teamId}`, { withCredentials: true });
      toast.success("Team deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete team");
    }
  };

  const resetForm = () => {
    setEditingTeam(null);
    setFormData({ name: "", description: "", color: "#002FA7" });
  };

  const getMemberCount = (teamId) => {
    return employees.filter(e => e.team_id === teamId).length;
  };

  const getUnassignedCount = () => {
    return employees.filter(e => !e.team_id).length;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="teams-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Teams</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              Organize employees into departments or teams
            </p>
          </div>
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded"
            data-testid="add-team-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Team
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 dark:border-zinc-800">
          <div className="bg-white dark:bg-zinc-900 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Teams</p>
            <p className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100 mt-1">{teams.length}</p>
          </div>
          <div className="bg-white dark:bg-zinc-900 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Total Employees</p>
            <p className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100 mt-1">{employees.length}</p>
          </div>
          <div className="bg-white dark:bg-zinc-900 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Unassigned</p>
            <p className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100 mt-1">{getUnassignedCount()}</p>
          </div>
        </div>

        {/* Teams Grid */}
        {teams.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-12 text-center">
            <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-zinc-400 dark:text-zinc-500" />
            </div>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">No teams yet</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-4">Create teams to organize your employees into departments</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 dark:border-zinc-800">
            {teams.map((team) => {
              const memberCount = getMemberCount(team.team_id);
              const members = employees.filter(e => e.team_id === team.team_id).slice(0, 5);
              return (
                <div key={team.team_id} className="bg-white dark:bg-zinc-900 p-6" data-testid={`team-${team.team_id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: team.color }}
                      />
                      <div>
                        <h3 className="text-base font-medium text-zinc-900 dark:text-zinc-100">{team.name}</h3>
                        {team.description && (
                          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{team.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(team)}
                        data-testid={`edit-team-${team.team_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(team.team_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-team-${team.team_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-zinc-100 dark:border-zinc-800 mb-3">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Members</span>
                    <span className="text-sm tabular-nums font-medium text-zinc-900 dark:text-zinc-100">{memberCount}</span>
                  </div>

                  {members.length > 0 ? (
                    <div className="space-y-2">
                      {members.map((emp) => (
                        <div key={emp.employee_id} className="flex items-center gap-2">
                          <Avatar className="w-6 h-6 border border-zinc-200 dark:border-zinc-800">
                            <AvatarFallback className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 text-xs">
                              {getInitials(emp.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm text-zinc-700 dark:text-zinc-300 truncate">{emp.name}</span>
                        </div>
                      ))}
                      {memberCount > 5 && (
                        <p className="text-xs text-zinc-400 dark:text-zinc-500">+{memberCount - 5} more</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-400 dark:text-zinc-500">No members assigned</p>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4 justify-between"
                    onClick={() => navigate(`/employees?team=${team.team_id}`)}
                    data-testid={`view-team-members-${team.team_id}`}
                  >
                    Manage Members <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Unassigned Employees */}
        {getUnassignedCount() > 0 && (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800">
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
              <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">Unassigned Employees</h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                These employees are not assigned to any team
              </p>
            </div>
            <div className="p-4 flex flex-wrap gap-2">
              {employees.filter(e => !e.team_id).map((emp) => (
                <Badge key={emp.employee_id} variant="secondary" className="text-sm font-normal py-1.5 px-3">
                  {emp.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-xl tracking-tight font-bold">
                {editingTeam ? "Edit Team" : "Create Team"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
              <div>
                <Label htmlFor="team-name" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Team Name *
                </Label>
                <Input
                  id="team-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Emergency Dept, Kitchen, Front Desk"
                  className="mt-1"
                  data-testid="team-name-input"
                />
              </div>
              <div>
                <Label htmlFor="team-desc" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Description
                </Label>
                <Input
                  id="team-desc"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this team"
                  className="mt-1"
                  data-testid="team-desc-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2 block">
                  Color
                </Label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      className={`w-8 h-8 rounded border-2 transition-all ${
                        formData.color === color.value ? 'border-zinc-900 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                      data-testid={`team-color-${color.label.toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 sm:col-span-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}
                  data-testid="cancel-team-btn">Cancel</Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="save-team-btn">
                  {editingTeam ? "Update" : "Create"} Team
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Teams;
